import React, { useMemo } from 'react';

import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
} from '../../util/types';
import { convertCategoriesToSelectTreeOptions, constructQueryParamName } from '../../util/search';

import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import NumberRangeFilter from './NumberRangeFilter/NumberRangeFilter';

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
/**
 * Helper to merge counts into options and filter out zero-count options
 */
const mergeCountsIntoOptions = (options, countsForFilter) => {
  if (!options || !countsForFilter) return options;

  return options
    .map(opt => {
      const optionKey = opt.option || opt.key;
      const count = countsForFilter[optionKey];
      return { ...opt, count };
    })
};

/**
 * Helper to merge counts into category tree options recursively
 * Each tree node has a 'level' property (1, 2, 3) that maps to categoryLevel1, categoryLevel2, etc.
 */
const mergeCategoryCountsIntoTree = (options, filterCounts) => {
  if (!options || !filterCounts) return options;

  // Check if counts have been loaded (categoryLevel1 exists in filterCounts)
  const countsLoaded = filterCounts.categoryLevel1 !== undefined;

  const addCountsRecursively = (opts, level = 1) => {
    const levelKey = `categoryLevel${level}`;
    const countsForLevel = filterCounts[levelKey] || {};

    return opts
      .map(opt => {
        // If counts are loaded, undefined means 0; otherwise keep as undefined
        const rawCount = countsForLevel[opt.option];
        const count = countsLoaded ? (rawCount || 0) : undefined;

        const suboptions = opt.suboptions
          ? addCountsRecursively(opt.suboptions, level + 1)
          : undefined;

        return {
          ...opt,
          count,
          ...(suboptions ? { suboptions } : {}),
        };
      });
  };

  return addCountsRecursively(options);
};

const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    marketplaceCurrency,
    intl,
    filterCounts,
    ...rest
  } = props;

  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;

  // Get counts for this specific filter
  const countsForFilter = filterCounts?.[key] || {};

  // Handle missing key
  if (!key) {
    console.warn('FilterComponent: Missing key', config);
    return null;
  }

  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-').toLowerCase();

  // Default filters: price, keywords, dates
  switch (schemaType) {
    case 'category': {
      const { scope, isNestedEnum, nestedParams } = config;
      const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope));
      const categoryOptions = convertCategoriesToSelectTreeOptions(listingCategories);
      const categoryOptionsWithCounts = mergeCategoryCountsIntoTree(categoryOptions, filterCounts);

      // Don't render category filter if there are no options with listings
      if (!categoryOptionsWithCounts || categoryOptionsWithCounts.length === 0) {
        return null;
      }

      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.categoryLabel' })}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={categoryOptionsWithCounts}
          isNestedEnum={isNestedEnum}
          {...rest}
        />
      );
    }
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          {...rest}
        />
      );
    }
    case 'keywords':
      // Keyword filter has been disabled
      return null;
    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          {...rest}
        />
      );
    }
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, filterType } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      // Merge counts into options and filter out zeros
      const optionsWithCounts = mergeCountsIntoOptions(enumOptions, countsForFilter);

      // Don't render filter if there are no options with listings
      if (!optionsWithCounts || optionsWithCounts.length === 0) {
        return null;
      }

      return filterType === 'SelectMultipleFilter' ? (
        <SelectMultipleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          schemaType={schemaType}
          {...rest}
        />
      ) : (
        <SelectSingleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          isNestedEnum={false}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      // Merge counts into options and filter out zeros
      const optionsWithCounts = mergeCountsIntoOptions(enumOptions, countsForFilter);

      // Don't render filter if there are no options with listings
      if (!optionsWithCounts || optionsWithCounts.length === 0) {
        return null;
      }

      return (
        <SelectMultipleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_LONG: {
      const { scope, filterConfig = {} } = config;
      const { label, min, max, step } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      return (
        <NumberRangeFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min || 0}
          max={max || 100}
          step={step || 1}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_BOOLEAN: {
      const { scope, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      // Boolean filter options - using string values 'true'/'false' for URL params
      const booleanOptions = [
        { option: 'true', label: intl.formatMessage({ id: 'FilterComponent.booleanTrue' }) },
        { option: 'false', label: intl.formatMessage({ id: 'FilterComponent.booleanFalse' }) },
      ];

      return (
        <SelectSingleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={booleanOptions}
          isNestedEnum={false}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
